import { createContext, useContext, useState, useEffect, ReactNode, useRef } from 'react';
import { Produit } from '../services/apiProduits';
import {
	getPanierEnCours,
	Panier,
	updatePanierEnCours,
	createPanierEnCours,
	StockModifie
} from "../services/apiPanier";
import { handleApiError } from "../utils/errorHandlers";
import { AxiosError } from 'axios';

const DEBOUNCE_DELAY = 800; // ms

interface PanierContextType {
	panier: Panier | null;
	stockModifies: { produits_retires: StockModifie[], produits_ajustes: StockModifie[] };
	loading: boolean;
	addToPanier: (product: Produit) => Promise<void>;
	removeFromPanier: (productId: number) => Promise<void>;
	handleQuantityChange: (id: number, change: number, maxQte: number) => Promise<void>;
	dupliquerPanier: (produits: Produit[]) => Promise<void>;
}

// Créer le contexte
const PanierContext = createContext<PanierContextType>({
	panier: null,
	stockModifies: { produits_retires: [], produits_ajustes: [] },
	loading: false,
	addToPanier: async () => {
	},
	removeFromPanier: async () => {
	},
	handleQuantityChange: async () => {
	},
	dupliquerPanier: async () => {
	},
});

// Fonction pour normaliser les produits (ajouter prix_num si manquant)
const normalizeProduits = (produits: Produit[]): Produit[] => {
	return produits.map(produit => ({
		...produit,
		prix_num: produit.prix_num ?? parseFloat(produit.prix?.toString() || '0'),
		quantite: produit.quantite ?? 0
	}));
};

// Hook personnalisé pour utiliser le contexte plus facilement
export const usePanier = () => useContext(PanierContext);

// Provider pour englober l'application
export const PanierProvider = ({ isConnected, children }: { isConnected: boolean, children: ReactNode }) => {
	const [panier, setPanier] = useState<Panier | null>(null);
	const [stockModifies, setStockModifies] = useState<{ produits_retires: StockModifie[], produits_ajustes: StockModifie[] }>({ produits_retires: [], produits_ajustes: [] });
	const [loading, setLoading] = useState(true);
	const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		const chargementPanierEnCours = async () => {
			try {
				setLoading(true);
				const panierEnCours = await getPanierEnCours();
				if (panierEnCours) {
					setStockModifies({
						produits_retires: panierEnCours.produits_retires,
						produits_ajustes: panierEnCours.produits_ajustes,
					});
					// Normaliser les produits pour s'assurer que prix_num est défini
					if (panierEnCours.panier) {
						panierEnCours.panier.produits = normalizeProduits(panierEnCours.panier.produits || []);
					}
					setPanier(panierEnCours.panier);
				}
			} catch (error) {
				console.error('Erreur lors du chargement du panier:', error);
				setStockModifies({ produits_retires: [], produits_ajustes: [] });
				setPanier(null);
			} finally {
				setLoading(false);
			}
		};
		if (isConnected) {
			chargementPanierEnCours();
		}
	}, []);

	// Ajouter un produit au panier
	const addToPanier = async (product: Produit) => {
		try {
			if (!panier) {
				// Créer un nouveau panier si aucun panier en cours
				const nouveauPanier = await createPanierEnCours({
					statut: 'BROUILLON',
					produits: [{ produits_id: product.id, quantite: 1 }]
				});
				// Normaliser les produits du nouveau panier
				nouveauPanier.produits = normalizeProduits(nouveauPanier.produits || []);
				setPanier(nouveauPanier);
				return;
			}

			const produitsExistants = panier.produits || [];
			const produitExistant = produitsExistants.find((p) => p.id === product.id);

			let nouveauxProduits;
			if (produitExistant) {
				// Incrémenter la quantité du produit si déjà présent
				nouveauxProduits = produitsExistants.map((p) =>
					p.id === product.id
						? { ...p, quantite: p.quantite + 1 }
						: p
				);
			} else {
				// Ajouter le produit avec une quantité de 1
				nouveauxProduits = [...produitsExistants, { ...product, quantite: 1 }];
			}

			// Convertir au format attendu par l'API
			const produitsApi = nouveauxProduits.map(p => ({
				produits_id: p.id,
				quantite: p.quantite
			}));

			const panierMisAJour = await updatePanierEnCours({
				produits: produitsApi
			});
			if (!panierMisAJour) return;

			// Normaliser les produits retournés par l'API
			panierMisAJour.produits = normalizeProduits(panierMisAJour.produits || []);
			setPanier(panierMisAJour);
		} catch (error) {
			handleApiError(error);
		}
	};

	// Supprimer un produit du panier
	const removeFromPanier = async (productId: number) => {
		if (!panier) return;

		try {
			const nouveauxProduits = (panier.produits || []).filter((p) => p.id !== productId);

			// Convertir au format attendu par l'API
			const produitsApi = nouveauxProduits.map(p => ({
				produits_id: p.id,
				quantite: p.quantite
			}));

			const panierMisAJour = await updatePanierEnCours({
				produits: produitsApi
			});
			if (!panierMisAJour) return;

			// Normaliser les produits retournés par l'API
			panierMisAJour.produits = normalizeProduits(panierMisAJour.produits || []);
			setPanier(panierMisAJour);
		} catch (error) {
			handleApiError(error);
		}
	};

	// Modifier la quantité d'un produit dans le panier
	const handleQuantityChange = async (id: number, change: number, maxQte: number) => {
		if (!panier) return;

		if (change > maxQte) {
			return;
		}

		// Calculer les nouveaux produits
		let nouveauxProduits;
		if (change <= 0) {
			// Supprimer le produit si la quantité est 0 ou négative
			nouveauxProduits = (panier.produits || []).filter((p) => p.id !== id);
		} else {
			// Modifier la quantité du produit
			nouveauxProduits = (panier.produits || []).map((p) =>
				p.id === id
					? { ...p, quantite: change }
					: p
			);
		}
		
		// Mettre à jour l'état local immédiatement pour un affichage réactif
		setPanier((prev) => ({
			...prev!,
			produits: nouveauxProduits,
		}));

		// --- SYNC AVEC L'API APRÈS UN DÉLAI ---
		// Annuler le timer précédent s'il existe
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		// Créer un nouveau timer pour synchroniser avec l'API
		debounceTimerRef.current = setTimeout(async () => {
			try {
				// Utiliser setPanier avec une fonction callback pour accéder à l'état le plus récent
				setPanier((currentPanier) => {
					if (!currentPanier) return currentPanier;

					// Convertir au format attendu par l'API
					const produitsApi = (currentPanier.produits || []).map(p => ({
						produits_id: p.id,
						quantite: p.quantite
					}));

					// Appeler l'API de manière asynchrone
					updatePanierEnCours({ produits: produitsApi })
						.then(panierMisAJour => {
							if (!panierMisAJour) return;
							// Normaliser les produits retournés par l'API
							panierMisAJour.produits = normalizeProduits(panierMisAJour.produits || []);
							setPanier(panierMisAJour);
						})
						.catch(err => {
							handleApiError(err);
						});

					// Retourner le panier actuel (pas de modification ici, l'API fera la mise à jour)
					return currentPanier;
				});
			} catch (err) {
				handleApiError(err);
			}
		}, DEBOUNCE_DELAY);
	};

	const dupliquerPanier = async (produits: Produit[]) => {
		// On ne commande que les produits en stock
		const produitACommander = [];
		for (const produit of produits) {
			if (produit.stockDisponible !== 0) {
				if (produit.quantite <= produit.stockDisponible) {
					produitACommander.push(produit);
				} else {
					produitACommander.push({
						id: produit.id,
						quantite: produit.stockDisponible
					});
				}
			}
		}
		
		const nouveauPanier = await createPanierEnCours({
			statut: 'BROUILLON',
			produits: produitACommander.map(p => ({
				produits_id: p.id,
				quantite: p.quantite
			}))
		});
		nouveauPanier.produits = normalizeProduits(nouveauPanier.produits || []);
		setPanier(nouveauPanier);
	};


	return (
		<PanierContext.Provider value={{ panier, stockModifies, loading, addToPanier, removeFromPanier, handleQuantityChange, dupliquerPanier }}>
			{children}
		</PanierContext.Provider>
	);
};

export function parseToCurrency(prix: number) {
	return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(prix)
}